'use client';

import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation';
import { EPlatform } from '@/utils/enums/platform-enum';

// 场景支持项接口定义
interface SceneSupportItem {
  image: string;
  word: string;
}

// SceneSupport 组件接口定义
interface BlueLightSceneSupportProps {
  supportData: {
    title?: string;
    description?: string;
    bottom_arr?: SceneSupportItem[];
  };
  deviceType?: EPlatform;
  isFromCache?: boolean;
  onSectionExp?: (params: any) => void;
}

export default function BlueLightSceneSupport({
  supportData,
  deviceType,
  isFromCache = false,
  onSectionExp,
}: BlueLightSceneSupportProps) {
  const isMobile = deviceType !== EPlatform.WEB;

  // 使用 useInView 进行模块曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.5, // 50%可见时触发
    triggerOnce: true, // 只触发一次
  });

  // isFromCache 现在通过参数传入，无需检测

  // 顶部标题动画 - 只在PC端执行
  const topTitleRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        // 标题动画
        const h2 = document.getElementsByClassName('support-top-title-h2');
        if (h2[0]) {
          h2[0].classList.add('activityFadeInUpAnimate');
        }

        // 描述动画
        const h5 = document.getElementsByClassName('support-top-title-h5');
        if (h5[0]) {
          h5[0].classList.add('activityFadeInUpAnimate');
        }
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.top-title .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.5, once: true },
  );

  // 场景列表动画 - 只在PC端执行
  const scenesListRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        // 列表项动画，错开显示
        const listItems = document.getElementsByClassName('support-top-title-li');
        Array.from(listItems).forEach((item, index) => {
          setTimeout(() => {
            item.classList.add('activityFadeInUpAnimate');
          }, index * 300); // 错开300ms
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.blue-light-support-part ul .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.5, once: true },
  );

  // 模块曝光埋点处理
  useEffect(() => {
    if (inView && onSectionExp) {
      onSectionExp({
        mkt_name: '各种场景支持',
      });
    }
  }, [inView, onSectionExp]);

  if (!supportData) return null;

  return (
    <div ref={expRef} className="support-part blue-light-support-part">
      {/* 顶部标题区域 */}
      <div
        ref={isMobile ? null : (topTitleRef as React.RefObject<HTMLDivElement>)}
        className={`top-title mx-auto text-center ${
          isMobile
            ? 'w-auto !mx-[24px] text-left pt-[28px] pb-[16px]'
            : 'w-[45.8vw] pt-[4.16vw] pb-[1.45vw]'
        }`}
      >
        {/* 主标题 */}
        {supportData.title && (
          <h2
            className={`font-bs text-white support-top-title-h2 ${
              isMobile
                ? 'text-[20px] leading-[28px]'
                : 'text-[20px] leading-[28px] xl:text-[2.08vw] xl:leading-[3.125vw] opacity0'
            }`}
            style={{
              background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {supportData.title}
          </h2>
        )}

        {/* 描述文字 */}
        {supportData.description && (
          <h5
            className={`text-[#B7B7B7] support-top-title-h5 ${
              isMobile
                ? 'mt-[14px] text-[11px] leading-[20px]'
                : 'mt-[14px] text-[11px] leading-[20px] xl:mt-[0.99vw] xl:text-[0.73vw] xl:leading-[1.35vw] opacity0'
            }`}
            dangerouslySetInnerHTML={{ __html: supportData.description }}
          />
        )}
      </div>

      {/* 场景支持列表 */}
      {supportData.bottom_arr && (
        <ul
          ref={isMobile ? null : (scenesListRef as React.RefObject<HTMLUListElement>)}
          className={`${
            isMobile
              ? 'flex flex-col px-[24px] pb-[32px]'
              : 'flex justify-between items-center px-[6.25vw] pb-[32px] xl:pb-[4.58vw]'
          }`}
        >
          {supportData.bottom_arr.map((item, index) => (
            <li
              key={index}
              className={`relative support-top-title-li ${
                isMobile ? 'w-full mb-[8px] last:mb-0' : 'opacity0'
              }`}
            >
              {/* 场景图片 */}
              <img
                src={item.image}
                alt={item.word}
                className={`h-auto ${isMobile ? 'w-full' : 'w-[28.5vw]'}`}
              />

              {/* 场景文字 - 叠加在图片底部 */}
              <h5
                className={`absolute bottom-0 left-0 w-full flex justify-center items-center text-[#B7B7B7] text-center ${
                  isMobile
                    ? 'h-[44px] text-[11px] leading-[20px]'
                    : 'h-[44px] xl:h-[3.3vw] text-[11px] leading-[20px] xl:text-[0.83vw] xl:leading-[1.35vw]'
                }`}
              >
                {item.word}
              </h5>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
